import React, { Component, ComponentClass } from "react"
import * as Sentry from "@sentry/browser"

import { FlexColumn } from "components/box"
import { SecondaryButton } from "components/buttons"
import { Paragraph } from "components/text"
import { SupportEmailLink } from "components/support-links"
import { H4 } from "components/text/headers"
import Icon from "components/icon"
import { isTestEnvironment } from "config/app"

type State = {
  hasError: boolean
  eventId: string
}

class ErrorBoundary extends Component<ComponentClass, State> {
  state = {
    hasError: false,
    eventId: ""
  }

  componentDidCatch(error: Error | null, errorInfo: object) {
    this.setState({ hasError: true })

    Sentry.withScope((scope: any) => {
      scope.setExtra("errorInfo", errorInfo)
      scope.setExtra("priority", "<APPLICATION CRASH DETECTED>")

      const eventId = Sentry.captureException(error)
      this.setState({ eventId })
    })
  }

  render() {
    const { eventId, hasError } = this.state

    if (hasError) {
      return (
        <FlexColumn justifyContent="center" height="100%" textAlign="center" py={5} px={4}>
          <Icon name="coveLogo" />
          <H4 mt={6} mb={4}>
            Something went wrong.
          </H4>

          <Paragraph>
            Please report the issue by using the button below, or you can reach out to us at <SupportEmailLink /> too.
            <br />
            We&apos;re sorry about the inconvenience!
          </Paragraph>

          <SecondaryButton mt={5} onClick={() => Sentry.showReportDialog({ eventId })}>
            Report feedback
          </SecondaryButton>
        </FlexColumn>
      )
    }

    return this.props.children
  }
}

/* We're disabling the ErrorBoundary in test envs so that if an error occurs during tests we have explicit information about it. */
export default isTestEnvironment ? React.Fragment : ErrorBoundary
